import {
  useCallback, useEffect, useMemo, useState
} from 'react'
import { usePromise } from 'react-use'
import { getSharingSessionWebsocketUrl } from './urls'
import SharingStatuses from './SharingStatuses'
import WebSocketConnectionError from './errors/WebSocketConnectionError'
import { logger } from './logger'

const DataNotificationTypes = { WEB_SOCKET: 'WEB_SOCKET' }
const SocketStatuses = {
  ERROR: 'ERROR',
  OPENED: 'OPENED',
  CLOSED: 'CLOSED',
  MESSAGE: 'MESSAGE'
}
const {
  FAILED_TO_CREATE,
  FAILED_TO_OPEN,
  ERROR_MESSAGE,
  CLOSED_UNEXPECTEDLY,
  SUBSCRIBE_TIMED_OUT
} = WebSocketConnectionError.Reasons

const READYSTATE_CHECK_MAX_ATTEMPT = 3
const READYSTATE_CHECK_INTERVAL = 2 * 1000
const DELAY_TO_SUBSCRIBE = 8 * 1000


const getLog = method => (name, ...elements) => {
  method.apply(logger, [`useSharingSessionConnection - ${name}:`, ...elements])
}

const log = getLog(logger.log)
const debug = getLog(logger.debug)
const warn = getLog(logger.warn)

function useSharingSessionConnection(
  sharingSessionDescriptor,
  actions,
  dataNotifier
) {
  const {
    messageWS: messageWSAction,
    getWS: getWSAction,
    getWSReadyState: getWSReadyStateAction,
    closeWS: closeWSAction
  } = actions
  const safeAsync = usePromise()
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [websocketId, setWebsocketId] = useState();
  const [receivedData, setReceivedData] = useState();
  const [receivedMessage, setReceivedMessage] = useState();
  const [sharingStatus, setSharingStatus] = useState('');
  const [profileToken, setProfileToken] = useState();
  const [error, setError] = useState(undefined);
  const [debouncedError, setDebouncedError] = useState(undefined);

  const websocketUrl = useMemo(() => {
    if (!sharingSessionDescriptor) return ''
    return getSharingSessionWebsocketUrl(sharingSessionDescriptor)
  }, [sharingSessionDescriptor]);

  const subscription = useMemo(() => {
    if (!sharingSessionDescriptor) return ''
    return sharingSessionDescriptor.getSessionDefinition()
  }, [sharingSessionDescriptor]);

  const reset = () => {
    setSharingStatus('')
    setSubscribing(false)
    setSubscribed(false)
    setConnecting(false)
    setConnected(false)
    setWebsocketId(undefined)
    setProfileToken(undefined)
    setError(undefined)
    setDebouncedError(undefined)
  }

  const connect = useCallback(
    () => {
      log('connect', { connected, connecting })
      if (!connecting && !connected) {
        log('connect starts')
        reset()
        setConnecting(true)

        const establishConnection = async () => {
          try {
            const { socketId } = await safeAsync(getWSAction(websocketUrl))
            if (!socketId) {
              throw new Error('Missing "socketId"!')
            }
            setWebsocketId(socketId)
          } catch (err) {
            setError(new WebSocketConnectionError(FAILED_TO_CREATE))
            throw err
          }
        }

        establishConnection()
          .catch((err) => {
            warn('connect establishConnection() failed', err)
          })
      }
    },
    [safeAsync, websocketUrl, getWSAction, connected, connecting]
  )

  const disconnect = useCallback(
    () => {
      debug('disconnect called', { connected, connecting })
      if (connected || connecting) {
        debug('disconnecting starts')
        // setConnecting(false)

        const endConnection = async () => {
          try {
            setWebsocketId(undefined)
            await safeAsync(closeWSAction(websocketId))
          } finally {
            setConnected(false)
          }
        }

        endConnection()
          .catch((err) => {
            warn('disconnect endConnection() failed', err)
          })
      }
    },
    [safeAsync, closeWSAction, websocketId, connected, connecting]
  )

  const subscribe = useCallback((asResuming = false) => {
    if (connected && subscription) {
      log('subscribe', { connected, subscription })
      setSubscribing(true)
      const payload = { subscription }
      if (asResuming) {
        payload.status = SharingStatuses.RESUMED
      }
      safeAsync(messageWSAction(websocketId, payload))
        .catch((err) => {
          warn('subscribe failed', err)
          setSubscribing(false)
        })
    }
  }, [safeAsync, connected, websocketId, subscription, messageWSAction])

  useEffect(() => {
    debug('useEffect[connected]', { connected })
    if (connected) {
      setConnecting(false)
    }
  }, [connected])

  // As soon as the websocket is created (websocketId exists),
  // pulls the websocket readyState until it is OPEN or max attempt
  // This is only useful to compensate rare case when the dataNotifier would not receive the OPENED message
  useEffect(() => {
    debug('useEffect[websocketId, connecting, connected]', { websocketId, connecting, connected })
    if (websocketId && connecting && !connected) {
      let attemptCount = 0
      const checkReadyState = async () => {
        attemptCount += 1
        debug('checkReadyState', { attemptCount })
        const { readyState } = await safeAsync(getWSReadyStateAction(websocketId))
          .catch((err) => {
            warn('checkReadyState failed', err)
          })
        debug('checkReadyState', { readyState })

        // Expect OPEN, ie equal 1 (https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/readyState)
        if (readyState === 1) {
          setConnected(true)
        } else if (attemptCount >= READYSTATE_CHECK_MAX_ATTEMPT) {
          setError(new WebSocketConnectionError(FAILED_TO_OPEN))
        }
      }
      const interval = setInterval(checkReadyState, READYSTATE_CHECK_INTERVAL)
      return () => {
        clearInterval(interval)
      }
    }
    return () => {}
  }, [safeAsync, websocketId, connected, connecting, getWSReadyStateAction])

  useEffect(() => {
    debug('useEffect[subscribing, subscribed]', { subscribing, subscribed })
    if (subscribing && !subscribed) {
      debug('useEffect[subscribing, subscribed] setting up time out')
      const checkSubscription = async () => {
        setError(new WebSocketConnectionError(SUBSCRIBE_TIMED_OUT))
      }
      const timeout = setTimeout(checkSubscription, DELAY_TO_SUBSCRIBE)
      return () => {
        clearTimeout(timeout)
      }
    }
    return () => {}
  }, [subscribing, subscribed])

  useEffect(() => {
    debug('useEffect[websocketId, dataNotifier]', { websocketId })

    const onDataHandler = ({ data } = {}) => {
      debug('useEffect[websocketId] onDataHandler', { data })

      const { type: dataType, payload: dataPayload } = data
      if (dataType === DataNotificationTypes.WEB_SOCKET) {
        setReceivedData(dataPayload)
        debug('useEffect[websocketId] onDataHandler, setReceivedData', dataPayload)
      }
    }

    if (websocketId) {
      dataNotifier.on('data', onDataHandler)
      return () => {
        debug('useEffect[websocketId]', 'removed data notifier listening!')
        return dataNotifier.off('data', onDataHandler)
      }
    }
    return () => {}
  }, [websocketId, dataNotifier])

  useEffect(() => {
    debug('useEffect[receivedData]', { receivedData })
    if (!receivedData) return

    const { socketStatus, message } = receivedData

    switch (socketStatus) {
      case SocketStatuses.ERROR:
        // Cosmetic delay
        setDebouncedError({
          error: new WebSocketConnectionError(ERROR_MESSAGE, message),
          delay: 500
        })
        break

      // Not expecting to receive it unless the connection is closed from our end, so unexpected
      // But can be received though... (while not yet disconnected and still listening...)
      // and Connect closes right after sending final message (COMPLETE/TIME_OUT/FAILED/REJECTED)
      // Hence, delaying ensures this error is only interpreted after a final message is received (and handled so the error will be ignored)
      case SocketStatuses.CLOSED:
        setDebouncedError({
          error: new WebSocketConnectionError(CLOSED_UNEXPECTEDLY),
          delay: 200
        })
        break

      case SocketStatuses.OPENED:
        setConnected(true)
        break

      case SocketStatuses.MESSAGE:
        setReceivedMessage(message)
        break
      default:
        break
    }
  }, [receivedData])

  useEffect(() => {
    debug('useEffect[receivedMessage, subscription]', { receivedMessage, subscription })
    if (!receivedMessage || !subscription) return

    const { subscription: messageSubscription, status, token } = receivedMessage

    if (messageSubscription !== subscription) {
      // ignoring non related messages...
    } else {
      switch (status) {
        case SharingStatuses.ACCEPTED:
        case SharingStatuses.RESUMED:
          setSubscribing(false)
          setSubscribed(true)
          break
        case SharingStatuses.FAILED:
        case SharingStatuses.REJECTED:
          setSubscribing(false)
          setSubscribed(false)
          break
        default:
          break
      }

      if (Object.values(SharingStatuses).includes(status)) {
        setSharingStatus(status)
      }

      if (token) {
        setProfileToken(token)
      }
    }
  }, [receivedMessage, subscription])

  useEffect(() => {
    if (debouncedError) {
      const { error: errorToBounce, delay } = debouncedError
      debug('useEffect[debouncedError] setting up planned timeout', delay)
      const debouncer = setTimeout(() => {
        setError(errorToBounce)
      }, delay)
      return () => {
        debug('useEffect[debouncedError] removing planned timeout')
        clearTimeout(debouncer)
      }
    }
    return () => {}
  }, [debouncedError])

  return {
    connected,
    connecting,
    subscribed,
    subscribing,
    connect,
    disconnect,
    subscribe,
    reset,
    sharingStatus,
    profileToken,
    error
  }
}

export default useSharingSessionConnection
